import { memo, useEffect, useRef, useState } from "react";
import { Divider, Grid2, IconButton, Typography } from "@mui/material";
import { Delete, EditNote, PersonAdd, Visibility } from "@mui/icons-material";
import { makeStyles } from "@mui/styles";
import { useNavigate } from "react-router-dom";

import Accordion from "../components/Accordion.js";
import Form from "../components/Form.js";
import Spinner from "../components/Spinner.js";
import Tooltip from "../components/Tooltip.js";
import Dialog from "../components/Dialog.js";
import Popup from "../components/Popup.js";
import Search from "../components/Search.js";
import { ThirdBackgroundButton } from "../components/Buttons.js";
import { isFuzzyMatch, useSnackbar } from "../utils/index.js";
import { getTeams, addMemberAdmin, removeTeamAdmin, updateTeamAdmin, removeMemberAdmin, createTeamAdmin } from "../api/index.js";

const useStyles = makeStyles((theme) => ({
	deleteIcon: {
		color: theme.palette.error.main,
	},
}));

const Teams = () => {
	const classes = useStyles();
	const navigate = useNavigate();
	const { error, success } = useSnackbar();
	const [teams, setTeams] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const [searchFilter, setSearchFilter] = useState("");
	const [displayTeams, setDisplayTeams] = useState([]);
	const [removeTeamWarningOpen, setRemoveTeamWarningOpen] = useState(null);
	const [addMemberPopupOpen, setAddMemberPopupOpen] = useState(null);
	const [teamNamePopupOpen, setTeamNamePopupOpen] = useState(null);
	const [removeMemberWarningOpen, setRemoveMemberWarningOpen] = useState(null);
	const [createTeamPopupOpen, setCreateTeamPopupOpen] = useState(false);

	const formRef = useRef(null);
	const addMemberFormRef = useRef(null);
	const teamNameFormRef = useRef(null);

	const fetchTeams = async () => {
		setIsLoading(true);
		const { success: fetchSuccess, teams: fetchedTeams, message } = await getTeams();
		if (fetchSuccess) {
			setTeams(fetchedTeams);
		} else {
			error(message);
		}

		setIsLoading(false);
	};
	
	const onSubmit = async (values) => {
		setIsLoading(true);

		try {
			const { success: createSuccess, message, errors } = await createTeamAdmin(values);
			if (createSuccess) {
				success(message);
				setCreateTeamPopupOpen(false);
				fetchTeams();
			} else if (formRef.current) {
				error(message);
				for (const err of errors) {
					formRef.current.setError(err.field, err.message);
				}
			}
		} catch {
			error("Something went wrong");
		}

		setIsLoading(false);
	};
	
	const onMemberSubmit = async (values) => {
		setIsLoading(true);

		try {
			const { success: addSuccess, message, errors } = await addMemberAdmin(addMemberPopupOpen.id, values);
			if (addSuccess) {
				success(message);
				setAddMemberPopupOpen(null);
				fetchTeams();
			} else if (addMemberFormRef.current) {
				error(message);
				for (const err of errors) {
					addMemberFormRef.current.setError(err.field, err.message);
				}
			}
		} catch {
			error("Something went wrong");
		}

		setIsLoading(false);
	};

	const onRemoveTeam = async () => {
		setIsLoading(true);

		try {
			const { success: removeSuccess, message } = await removeTeamAdmin(removeTeamWarningOpen.id);
			if (removeSuccess) {
				success(message);
				fetchTeams();
			} else {
				error("Something went wrong");
			}
		} catch {
			error("Something went wrong");
		}

		setIsLoading(false);
	};

	const onRemoveMember = async () => {
		setIsLoading(true);

		try {
			const teamId = removeMemberWarningOpen.team.id;
			const userId = removeMemberWarningOpen.member.id;
			const { success: removeSuccess, message } = await removeMemberAdmin(teamId, userId);
			if (removeSuccess) {
				success(message);
				fetchTeams();
			} else {
				error("Something went wrong");
			}
		} catch {
			error("Something went wrong");
		}

		setIsLoading(false);
	};

	const onTeamSubmit = async (values) => {
		setIsLoading(true);

		try {
			const { success: updateSuccess, message, errors } = await updateTeamAdmin(teamNamePopupOpen.id, values);
			if (updateSuccess) {
				success(message);
				setTeamNamePopupOpen(null);
				fetchTeams();
			} else if (teamNameFormRef.current) {
				error(message);
				for (const err of errors) {
					teamNameFormRef.current.setError(err.field, err.message);
				}
			}
		} catch {
			error("Something went wrong");
		}

		setIsLoading(false);
	};

	const formContent = [
		{
			customType: "input",
			id: "name",
			type: "text",
			label: "Team Name",
			placeholder: "Team Name",
		},
		{
			customType: "input",
			id: "user1",
			type: "email",
			label: "First Team Member",
			placeholder: "User Email",
		},
		{
			customType: "input",
			id: "user2",
			type: "email",
			label: "Second Team Member (optional)",
			placeholder: "User Email",
		},
		{
			customType: "input",
			id: "user3",
			type: "email",
			label: "Third Team Member (optional)",
			placeholder: "User Email",
		},
		{
			customType: "input",
			id: "user4",
			type: "email",
			label: "Fourth Team Member (optional)",
			placeholder: "User Email",
		},
		{
			customType: "input",
			id: "user5",
			type: "email",
			label: "Fifth Team Member (optional)",
			placeholder: "User Email",
		},
		{
			customType: "button",
			id: "submit",
			type: "submit",
			text: "Submit",
			color: "third",
		},
	];

	const addPersonFormContent = [
		{
			customType: "input",
			id: "email",
			type: "email",
			label: "User Email",
			placeholder: "User Email",
		},
		{
			customType: "button",
			id: "submit",
			type: "submit",
			text: "Add",
			color: "third",
		},
	];

	const teamNameFormContent = [
		{
			customType: "input",
			id: "name",
			type: "text",
			label: "Team Name",
			value: teamNamePopupOpen?.name,
			placeholder: "Team Name",
		},
		{
			customType: "button",
			id: "submit",
			type: "submit",
			text: "Submit",
			color: "third",
		},
	];

	useEffect(() => {
		fetchTeams();
	}, []);

	useEffect(() => {
		setDisplayTeams(teams.filter((team) => isFuzzyMatch(team.name, searchFilter)
			|| team.members.some((member) => isFuzzyMatch(member.username, searchFilter))
			|| team.members.some((member) => isFuzzyMatch(member.fullname, searchFilter))
			|| team.members.some((member) => isFuzzyMatch(member.email, searchFilter))));
	}, [searchFilter, teams]);
	
	return (
		<>
			<Spinner open={isLoading} />
			<Grid2 container display="flex" direction="column" justifyContent="center" alignItems="center">
				<Grid2 width="100%" display="flex" justifyContent="space-between">
					<ThirdBackgroundButton onClick={() => setCreateTeamPopupOpen(true)} title="Create Team" />
					<Search width="500px" value={searchFilter} onChange={(event) => setSearchFilter(event.target.value)} />
				</Grid2>
				{displayTeams.map((team) => (
					<Grid2 key={team?.name} mt={2} width="100%">
						<Accordion
							alwaysExpanded
							title={(
								<Grid2
									container
									width="100%"
									display="flex"
									direction="row"
									alignItems="center"
									justifyContent="space-between"
								>
									<Typography>{team.name}</Typography>
									<Grid2>
										<Tooltip title="See Team Tasks">
											<IconButton onClick={() => navigate(`/tasks/${team.id}`)}>
												<Visibility />
											</IconButton>
										</Tooltip>
										<Tooltip title="Edit Team Name">
											<IconButton onClick={() => setTeamNamePopupOpen(team)}>
												<EditNote />
											</IconButton>
										</Tooltip>
										<Tooltip title="Add Member">
											<IconButton onClick={() => setAddMemberPopupOpen(team)}>
												<PersonAdd />
											</IconButton>
										</Tooltip>
										<Tooltip title="Delete Team">
											<IconButton onClick={() => setRemoveTeamWarningOpen(team)}>
												<Delete />
											</IconButton>
										</Tooltip>
									</Grid2>
								</Grid2>
							)}
							subtitle={(
								<Grid2 container display="flex" direction="row" alignItems="center" justifyContent="center" spacing={0}>
									<Grid2 size={{ xs: 3 }}>
										<Typography>{"Username"}</Typography>
									</Grid2>
									<Grid2 size={{ xs: 4 }} textAlign="center">
										<Typography>{"Full Name"}</Typography>
									</Grid2>
									<Grid2 size={{ xs: 4 }}>
										<Typography>{"E-mail"}</Typography>
									</Grid2>
									<Grid2 size={{ xs: 1 }} />
								</Grid2>
							)}
							content={(
								<Grid2 container width="100%" display="flex" direction="column" alignItems="center" justifyContent="center" spacing={0} color="black">
									{team.members.map((us, ind) => (
										<Grid2 key={`comp_${ind}`} flexDirection="column" width="100%">
											{ind !== 0
											&& (
												<Grid2 key={`divider_${ind}`} my={1} width="100%">
													<Divider style={{ width: "100%" }} />
												</Grid2>
											)}
											<Grid2 key={`row_${ind}`} container alignItems="center">
												<Grid2 size={{ xs: 3 }}>
													<Typography>{us.username}</Typography>
												</Grid2>
												<Grid2 size={{ xs: 4 }} textAlign="center">
													<Typography>{us.fullname}</Typography>
												</Grid2>
												<Grid2 size={{ xs: 4 }}>
													<Typography>{us.email}</Typography>
												</Grid2>
												<Grid2 size={{ xs: 1 }} justifyContent="flex-end" display="flex">
													<Tooltip title="Remove Member">
														<IconButton onClick={() => setRemoveMemberWarningOpen({ team, member: us })}>
															<Delete className={classes.deleteIcon} />
														</IconButton>
													</Tooltip>
												</Grid2>
											</Grid2>
										</Grid2>
									))}
								</Grid2>
							)}
						/>
					</Grid2>
				))}
				{teams.length === 0 && (
					<Grid2 container direction="column" alignItems="center" justifyContent="center">
						<Typography variant="h5" sx={{ color: "primary.main", p: 1 }}>{"No teams found!"}</Typography>
					</Grid2>
				)}
			</Grid2>
			<Dialog
				open={removeTeamWarningOpen !== null}
				title="Remove Team"
				text="Are you sure you want to remove the team? This action cannot be undone."
				confirmButton="Remove"
				cancelButton="Cancel"
				onConfirm={() => {
					setRemoveTeamWarningOpen(null);
					onRemoveTeam();
				}}
				onClose={() => setRemoveTeamWarningOpen(null)}
			/>
			<Dialog
				open={removeMemberWarningOpen !== null}
				title="Remove Member"
				text={`Are you sure you want to remove ${removeMemberWarningOpen?.member.username} from the team ${removeMemberWarningOpen?.team.name}?`}
				confirmButton="Remove"
				cancelButton="Cancel"
				onConfirm={() => {
					setRemoveMemberWarningOpen(null);
					onRemoveMember();
				}}
				onClose={() => setRemoveMemberWarningOpen(null)}
			/>
			<Popup
				width="400px"
				title="Add Member"
				open={addMemberPopupOpen !== null}
				onClose={() => setAddMemberPopupOpen(null)}
			>
				<Form ref={addMemberFormRef} content={addPersonFormContent} validationSchema="addMember" onSubmit={onMemberSubmit} />
			</Popup>
			<Popup
				width="400px"
				title="Edit Team Name"
				open={teamNamePopupOpen !== null}
				onClose={() => setTeamNamePopupOpen(null)}
			>
				<Form enableReinitialize ref={teamNameFormRef} content={teamNameFormContent} validationSchema="teamName" onSubmit={onTeamSubmit} />
			</Popup>
			<Popup
				width="400px"
				title="Create Team"
				open={createTeamPopupOpen}
				onClose={() => setCreateTeamPopupOpen(false)}
			>
				<Form ref={formRef} content={formContent} validationSchema="teamCreationAdmin" onSubmit={onSubmit} />
			</Popup>
		</>
	);
};

export default memo(Teams);
